import { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, CustomButton, CustomDataTable, dayjs, Dayjs, Default_Text_Color, doc_icon, DownloadIcon, FilterCard, Toaster, Typography, useApi, validationMessages } from "../../utils/globalImports";
import React from "react";

interface AttachmentComponentProps {
  entityTypeId?: number; // Project, Proposal, FeeStage
  entityId?: number; // Id of project/Proposal
  resourceId: number;
  title: string;
  searchbarText: string;
}

interface Attachment {
  AttachmentId: number;
  id: number;
  AttachmentName: string;
  Uploaded_date: Dayjs;
  Uploaded_by: string;
  base64data?: string; // Optional property for base64 data (for download)
  filename?: string;
  filetype?: string;

}

const AttachmentComponent: React.FC<AttachmentComponentProps> = ({
  entityTypeId,
  entityId,
  resourceId,
  title,
  searchbarText,
}) => {

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedAttachment, setSelectedAttachment] = useState<File | null>(null); // State to hold the selected file
  const [selectedAttachementRow, setSelectedAttachementRow] = React.useState<Attachment | null>(null); // State to hold the selected attachment row for highlighting
  const [AttachmentDataList, setAttachementDataList] = React.useState<Attachment[]>([]); // State to hold the list of attachments
  const [filteredData, setFilteredData] = React.useState<any[]>([]); // State to hold the filtered data
  const [isOn, setIsOn] = React.useState<boolean>(false); // State to manage toggle state
  const [dialogOpen, setDialogOpen] = React.useState<boolean>(false); // State to manage dialog visibility
  const [dataLoaded, setDataLoaded] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    type: "success",
  });
  const { execute, loading } = useApi();
  const userId = Number(localStorage.getItem("userId"));
  const sortOptions = [
    { value: "Upload Date_desc", text: "Latest" },
    { value: "Upload Date_asc", text: "Oldest" },
  ];

  const props = {
    showNotesComments: true,
    title: "Proposal Attachments",
    actionText: "",
    searchbarText: validationMessages.uploadedDocuments_searchText,
    showActiveFilter: false,
  };

  const fetchAttachmentData = async () => {
    try {
      const { data } = await execute<any[]>(
        "get",
        `/attachment?entityTypeId=${entityTypeId}&entityId=${entityId}`
      );


      if (data && Array.isArray(data) && data.length > 0) {
        const mappedData: Attachment[] = data.map((item) => ({
          AttachmentId: item.attachmentId,
          id: item.attachmentId,
          AttachmentName: item.filename,
          Uploaded_date: dayjs.utc(item.createdAt).local(),
          Uploaded_by: item.createdName,
          base64Data: item.base64data,
          filename: item.filename,
          filetype: item.filetype
        }));

        setAttachementDataList(mappedData);
        showGrid(mappedData);
      }
    } catch (error) {
      console.error("Error fetching attachment data:", error);
      setToast({
        open: true,
        message: "Failed to fetch attachments",
        type: "error",
      });
    } finally {
      setDataLoaded(true); // ✅ API finished at least once
    }
  };

  const handleSearchGridData = (
    query: string,
    status?: number,
    category?: number,
    region?: number,
    contactType?: number,
    date?: string
  ) => {
    const lowerCaseQuery = query.toLowerCase();
    const filtered = AttachmentDataList.filter((item) => {
      const matchesQuery =
        item.AttachmentName.toLowerCase().includes(lowerCaseQuery) ||
        item.Uploaded_by?.toString().toLowerCase().includes(lowerCaseQuery);

      const matchDate = date
        ? dayjs(item.Uploaded_date).isSame(dayjs(date), "day")
        : true;

      return matchesQuery && matchDate;
    });
    setFilteredData(filtered);
    showGrid(filtered);
  };

  const showGrid = (dataList: Attachment[], status = [1]) => {
    const activeAttachment = dataList
      .map((attachment) => {
        const {
          AttachmentId,
          id,
          AttachmentName,
          Uploaded_date,
          Uploaded_by,
        } = attachment; //
        return {
          id: id,
          AttachmentId: AttachmentId,
          "Uploaded Document": AttachmentName,
          "Upload Date": Uploaded_date
            ? dayjs(Uploaded_date).local().format("DD/MM/YYYY HH:mm:ss")
            : "",
          "Uploaded By": Uploaded_by,
        };
      })
      .sort((a, b) => {
        const dateA = dayjs(a["Upload Date"], "DD/MM/YYYY HH:mm:ss").valueOf();
        const dateB = dayjs(b["Upload Date"], "DD/MM/YYYY HH:mm:ss").valueOf();
        return dateB - dateA; // Sort in descending order
      });

    setFilteredData(activeAttachment);
  };

  const handleToggleState = (row: any) => {
    // if (row.Status === "Inactive") {
    //   setIsOn(false);
    // } else {
    //   setIsOn(true);
    // }
    // setSelectedAttachementRow(row);
    // setDialogOpen(true);
  };

  const handleDelete = (row: any) => {
    setSelectedAttachementRow(row);
    setDialogOpen(true);
  }

  const handleDialogConfirm = async () => {
    if (!selectedAttachementRow) return;

    try {
      const attachmentId = selectedAttachementRow.id;

      // Call DELETE API
      const response = await execute(
        "delete",
        `attachment/${attachmentId}`
      );

      if (response?.error) {
        setToast({ open: true, message: response.error, type: "error" });
      } else {
        setToast({
          open: true,
          message: "Attachment deleted successfully!",
          type: "success",
        });

        setAttachementDataList([]);
        showGrid([]);
        // Refresh the list
        fetchAttachmentData();
      }
    } catch (error) {
      console.error("Error deleting attachment:", error);
      setToast({
        open: true,
        message: "Failed to delete attachment",
        type: "error",
      });
    } finally {
      setDialogOpen(false);
      setSelectedAttachementRow(null);
    }
  };

  //
  const handleDialogCancel = () => {
    setDialogOpen(false);
    setSelectedAttachementRow(null);
  };

  useEffect(() => {
    fetchAttachmentData();
  }, []);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedAttachment(null);
      setToast({ open: true, message: "No file selected", type: "error" });
      return;
    }

    const validFileTypes = [
      "application/pdf", //pdf
      "image/jpeg", //jpeg
      "image/png", //png
      "application/msword", // .doc
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
      "text/csv", // .csv
      "application/vnd.ms-excel", // .xls
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
    ];
    const maxFileSizeMB = 2;
    const fileSizeInMB = file.size / (1024 * 1024);

    if (fileSizeInMB > maxFileSizeMB) {
      setToast({
        open: true,
        message: `file should not exceeds the maximum size of ${maxFileSizeMB}MB.`,
        type: "error",
      });
      return;
    }

    if (!validFileTypes.includes(file.type)) {
      setToast({
        open: true,
        message: `${file.name} is not a supported file type.`,
        type: "error",
      });
      return;
    }

    setSelectedAttachment(file);
  };

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setSelectedAttachment(null);
  };

  const handleAttachmentSubmit = async () => {
    if (!selectedAttachment) {
      setToast({
        open: true,
        message: "Please select a file to upload",
        type: "error",
      });
      return;
    }

    const reader = new FileReader();

    reader.onloadend = async () => {
      const base64String = (reader.result as string).split(",")[1]; // remove data:*/*;base64, prefix
      const timestamp = new Date().toISOString();

      const payload = {
        entityId: entityId,
        entityTypeId: entityTypeId,
        base64Data: base64String,
        createdId: userId,
        createdAt: timestamp,
        fileName: selectedAttachment.name,
        fileType: selectedAttachment.type,
      };

      try {
        const response = await execute("post", "attachment", payload);
        if (response?.error) {
          setToast({ open: true, message: response.error, type: "error" });
        } else {
          setToast({
            open: true,
            message: "File uploaded successfully!",
            type: "success",
          });
          resetFileInput(); // Reset file input after successful upload
          setSelectedAttachment(null);
          fetchAttachmentData(); // optionally refresh list
        }
      } catch (error) {
        console.error("Upload failed:", error);
        setToast({ open: true, message: "Upload failed", type: "error" });
      }
    };

    reader.onerror = () => {
      setToast({ open: true, message: "Failed to read file", type: "error" });
    };

    reader.readAsDataURL(selectedAttachment); // triggers reader.onloadend
  };

  const handleEdit = (row: Attachment) => {
    setSelectedAttachementRow(row);
    setDialogOpen(true);
  }; //TODO: Implement delete functionality

  const handleDownload = async (row: Attachment) => {
    try {
      const response = await execute<Attachment>("get", `attachment/${row.id}`);

      // Check if the response contains the necessary data
      if (response && response.data) {
        const { base64data, filename, filetype } = response.data;

        // Step 2: Check if base64Data exists and is a string
        if (!base64data || typeof base64data !== "string") {
          setToast({ open: true, message: "Invalid file", type: "error" });
          return;
        }

        // Use a default filename if fileName is undefined
        const downloadFileName = filename || "untitled-file";

        // Step 3: Convert the base64 string to a binary string (byte data)
        const byteCharacters = atob(base64data); // Decode base64 to raw binary string
        const byteArrays = [];

        // Convert the binary string into a byte array (Uint8Array)
        for (let offset = 0; offset < byteCharacters.length; offset += 512) {
          const slice = byteCharacters.slice(offset, offset + 512);
          const byteNumbers = new Array(slice.length);
          for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          byteArrays.push(byteArray);
        }

        // Create a Blob from the byte array
        const blob = new Blob(byteArrays, { type: filetype });

        // Step 4: Create a temporary URL for the Blob
        const downloadUrl = URL.createObjectURL(blob);

        // Step 5: Create an anchor element for the download
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = downloadFileName; // Use the default fileName if it's undefined

        // Trigger the download by programmatically clicking the link
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Optionally, revoke the object URL after the download is initiated to free up memory
        URL.revokeObjectURL(downloadUrl);
      } else {
        // If no data was returned, show an error message
        setToast({
          open: true,
          message: "Failed to fetch attachment data",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Error downloading attachment:", error);
      setToast({
        open: true,
        message: "Error downloading the attachment",
        type: "error",
      });
    }
  };


  return (
    <Box sx={{ flexGrow: 1 }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: "16px",
          marginBottom: "30px",
        }}
      >
        <Typography
          style={{
            boxSizing: "border-box",
            color: Default_Text_Color,
            textAlign: "left",
            lineHeight: "normal",
            fontWeight:600,
            fontSize:"18px"
          }}
        >
          Attachments
          <Typography
          sx={{fontSize:14,color:"grey"}}
          >
            Upload and manage all project related attachments.
          </Typography>
        </Typography>
        {/* ------------------------------------- */}
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "16px" }}>
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            style={{ display: "none" }}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.png"
            onChange={handleFileChange}
          />

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "800px",
              height: "80px",
              padding: "0 12px 0 16px",
              borderRadius: "12px",
              boxSizing: "border-box",
              cursor: "default",
              transition: "border-color 0.2s, background-color 0.2s",
              border:"2px solid lightgrey"
            }}
          >
            {/* Left: icon + text */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {/* Cloud upload icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#9e9e9e"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 14a4 4 0 0 1 0-8h.5A5.5 5.5 0 0 1 16 8.5V9a4 4 0 0 1 .5 7.97" />
                <path d="M12 13v8" />
                <path d="m9 16 3-3 3 3" />
              </svg>

              <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.3" }}>
                {selectedAttachment ? (
                  <span style={{ fontSize: "13px", fontFamily: "Poppins, sans-serif", color: "#000" }}>
                    {selectedAttachment.name}
                  </span>
                ) : (
                  <>
                    <span style={{ fontSize: "13px", fontFamily: "Poppins, sans-serif", color: "#000" }}>
                      <span
                        onClick={handleClick}
                        style={{ color: "#1976d2", cursor: "pointer", fontWeight: 500 }}
                      >
                        Choose file
                      </span>{" "}
                      or drag and drop
                    </span>
                    <span style={{ fontSize: "11px", fontFamily: "Poppins, sans-serif", color: "#9e9e9e" }}>
                      Supports: PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (Max. 20MB)
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Right: Browse button */}
            <button
              type="button"
              onClick={handleClick}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 14px",
                borderRadius: "8px",
                border: "1px solid #c0c0c0",
                backgroundColor: "#fff",
                fontFamily: "Poppins, sans-serif",
                fontSize: "13px",
                color: "#333",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {/* Folder icon */}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7z" />
              </svg>
              Browse
            </button>
          </div>
          {/* Add Attachment button */}
          <CustomButton
            capsule
            type="button"
            sx={{
              textTransform: "none",
              whiteSpace: "nowrap",
              width: "200px",
              height: "56px",
            }}
            onClick={handleAttachmentSubmit}
          >
            + Add Attachment
          </CustomButton>
        </div>
        {/* ------------------------------------------- */}

      </div>

      <FilterCard {...props} handleSearch={handleSearchGridData} showDateFilter={false} ignoreAttachmentDate={true} ></FilterCard>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}>
          <CircularProgress size={28} />
        </Box>
      ) : (AttachmentDataList.length > 0) ? (
        <CustomDataTable
          rows={filteredData}
          actionText="attachment"
          sortOptions={sortOptions}
          deleteFunction={true}
          onRowEdit={handleEdit}
          onRowDelete={handleDelete}
          onToggleClick={handleToggleState}
          showEditIcon={false}
          isDialogOpen={dialogOpen}
          isToggleOn={isOn}
          handleCancel={handleDialogCancel}
          handleConfirm={handleDialogConfirm}
          // gridHeight="calc(100vh - 200px)"
          customActionIcon={<DownloadIcon sx={{ color: "#a3c63d" }} />}
          onCustomActionClick={handleDownload}
          noResultMessage={validationMessages.common_RecordNotFound(
            "Attachment"
          )}
        ></CustomDataTable>
      ) : dataLoaded ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', justifyContent: 'center', alignItems: 'center', height: '30vh' }}>
          <Typography sx={{ color: Default_Text_Color, marginTop: '2rem' }}> There are no attachments to display</Typography>
        </Box>
      ) : null}
      <Toaster
        open={toast.open}
        message={toast.message}
        type={toast.type as "success" | "error"}
        onClose={() => setToast({ ...toast, open: false })}
      />
    </Box>
  );
};

export default AttachmentComponent;